import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';

// --- 1. MODERN IMPORTS ---
import { ActivatedRouteSnapshot, DetachedRouteHandle, PreloadAllModules, Router, RouteReuseStrategy, Routes } from '@angular/router'; // Likely needed for tabs/navigation in modern Ionic
import { IonicModule, IonicRouteStrategy } from '@ionic/angular'; // Use the modern path for IonicModule
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HttpClientModule, HttpClient } from '@angular/common/http';
import { RouterModule } from '@angular/router';
import { IonicStorageModule } from '@ionic/storage-angular';

// --- 2. NATIVE PLUGINS (Should almost exclusively be in providers or component constructors) ---
// import { InAppBrowser } from '@ionic-native/in-app-browser/ngx'; // Use /ngx suffix for compatibility
import { Network } from '@ionic-native/network/ngx';
// import { StatusBar } from '@ionic-native/status-bar/ngx';
// import { SplashScreen } from '@ionic-native/splash-screen/ngx';
import { GooglePlus } from '@ionic-native/google-plus/ngx';
import { Facebook } from '@ionic-native/facebook/ngx';
import { AppVersion } from '@ionic-native/app-version/ngx';

// --- 3. APPLICATION / PAGES / COMPONENTS / PROVIDERS ---
import { MyApp } from './app.component';



// --- COMPONENTS ---
// ... KEEP ALL COMPONENT IMPORTS ... (omitted for brevity)

// --- PROVIDERS ---
// Since you fixed WishlistServiceProvider to use providedIn: 'root', most providers can be removed from here.
import { NewsfeedServiceProvider } from '../providers/newsfeed-service/newsfeed-service';
import { AnalysisServiceProvider } from '../providers/analysis-service/analysis-service';
import { BeerServiceProvider } from '../providers/beer-service/beer-service';
import { WishlistServiceProvider } from '../providers/wishlist-service/wishlist-service';
import { AnalysisHolderProvider } from '../providers/analysis-holder/analysis-holder';
import { LocationServiceProvider } from '../providers/location-service/location-service';
import { UserDataProvider } from '../providers/user-data/user-data';
import { RefdataProvider } from '../providers/refdata/refdata';
import { SvgProvider } from '../providers/svg/svg';
import { AuthProvider } from '../providers/auth/auth';
import { SpiderSvgProvider } from '../providers/spider-svg/spider-svg';
import { AwsProvider } from '../providers/aws/aws';
import { BhutilProvider } from '../providers/bhutil/bhutil';
import { BeerHiveButtonProvider } from '../providers/beer-hive-button/beer-hive-button';
import { BeerHiveSearchProvider } from '../providers/beer-hive-search/beer-hive-search';
import { BeerHiveAnalysisProvider } from '../providers/beer-hive-analysis/beer-hive-analysis';
import { EnumsProvider } from '../providers/enums/enums';
import { BeerCcButtonProvider } from '../providers/beer-cc-button/beer-cc-button';
import { BeerCcSearchProvider } from '../providers/beer-cc-search/beer-cc-search';
import { BeerCcAnalysisProvider } from '../providers/beer-cc-analysis/beer-cc-analysis';
import { SearchProvider } from '../providers/search/search';

// --- PAGES ---
import { AboutPage } from '../pages/about/about';
import { AuthPage } from '../pages/auth/auth';
import { LandingPage } from '../pages/landing/landing';
import { ApploginPage } from '../pages/applogin/applogin';
import { WebloginPage } from '../pages/weblogin/weblogin';
import { HomePage } from '../pages/home/home';
import { WishlistPage } from '../pages/wishlist/wishlist';
import { AnalysesPage } from '../pages/analyses/analyses';
import { FaqPage } from '../pages/faq/faq';
import { AddBeerFormPage } from '../pages/add-beer-form/add-beer-form';
import { AddBreweryFormPage } from '../pages/add-brewery-form/add-brewery-form';
import { BeerDetailPage } from '../pages/beer-detail/beer-detail';
import { ContactPage } from '../pages/contact/contact';
import { SearchPage } from '../pages/search/search';
import { SearchBreweryPage } from '../pages/search-brewery/search-brewery';
import { SelectorPopoverPage } from '../pages/selector-popover/selector-popover';
import { TabsPage } from '../pages/tabs/tabs';
import { VenueSelectionPopoverPage } from '../pages/venue-selection-popover/venue-selection-popover';
import { CommonModule } from '@angular/common';
//import { AddBeerFormPageModule } from '../pages/add-beer-form/add-beer-form.module';
import { LandingPage_PATH, AboutPage_PATH, BeerDetailPage_PATH, AddBeerFormPage_PATH, AddBreweryFormPage_PATH, AnalysesPage_PATH, ApploginPage_PATH, AuthPage_PATH, ContactPage_PATH, FaqPage_PATH, HomePage_PATH, SearchPage_PATH, SearchBreweryPage_PATH, SelectorPopoverPage_PATH, TabsPage_PATH, VenueSelectionPopoverPage_PATH, WebloginPage_PATH, WishlistPage_PATH } from './app-constants';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { routes } from './app.routes';
import { NavBarComponent } from '../components/nav-bar/nav-bar';
import { StatBarComponent } from '../components/stat-bar/stat-bar';
import { HighestRatedBeersComponent } from '../components/highest-rated-beers/highest-rated-beers';
import { BeerEventsComponent } from '../components/beer-events/beer-events';
import { BeerHiveLogoComponent } from '../components/beer-hive-logo/beer-hive-logo';
import { BeerListEntryComponent } from '../components/beer-list-entry/beer-list-entry';
import { AddBeerComponent } from '../components/add-beer/add-beer';
import { AddBeerHeaderComponent } from '../components/add-beer-header/add-beer-header';
import { AddBreweryHeaderComponent } from '../components/add-brewery-header/add-brewery-header';
import { AnalysesHeaderComponent } from '../components/analyses-header/analyses-header';
import { BeerAnalysisComponent } from '../components/beer-analysis/beer-analysis';
import { BeerAnalysisMyFlavorComponent } from '../components/beer-analysis-my/beer-analysis-my-flavor';
import { BeerAnalysisMyMouthfeelComponent } from '../components/beer-analysis-my/beer-analysis-my-mouthfeel';
import { BeerAnalysisMyRatingNotesComponent } from '../components/beer-analysis-my/beer-analysis-my-ratingnotes';
import { BeerAnalysisMySummaryComponent } from '../components/beer-analysis-my/beer-analysis-my-summary';
import { BeerAnalysisGroupComponent } from '../components/beer-analysis-group/beer-analysis-group';
import { BeerAromasComponent } from '../components/beer-aromas/beer-aromas';
import { BeerCcComponent } from '../components/beer-cc/beer-cc';
import { BeerHiveComponent } from '../components/beer-hive/beer-hive';
import { BeerHiveButtonComponent } from '../components/beer-hive-button/beer-hive-button';
import { BeerIbaComponent } from '../components/beer-iba/beer-iba';
import { BeerInfoComponent } from '../components/beer-info/beer-info';
import { BeerSpiderComponent } from '../components/beer-spider/beer-spider';
import { ColorClarityComponent } from '../components/color-clarity/color-clarity';
import { FaqViewerComponent } from '../components/faq-viewer/faq-viewer';
import { FiltersHeaderComponent } from '../components/filters-header/filters-header';
import { FlavorSliderSearchComponent } from '../components/flavor-slider/flavor-slider-search';
import { FlavorSliderAnalysisComponent } from '../components/flavor-slider/flavor-slider-analysis';
import { FoundBeerComponent } from '../components/found-beer/found-beer';
import { MenuComponent } from '../components/menu/menu';
import { MyAnalysesComponent } from '../components/my-analyses/my-analyses';
import { MyNotesComponent } from '../components/my-notes/my-notes';
import { RatingBarComponent } from '../components/rating-bar/rating-bar';
import { SimilarBeersComponent } from '../components/similar-beers/similar-beers';
import { SmileRateComponent } from '../components/smile-rate/smile-rate';
import { TextSearchBarComponent } from '../components/text-search-bar/text-search-bar';
import { WishlistComponent } from '../components/wishlist/wishlist';
import { WishlistButtonComponent } from '../components/wishlist-button/wishlist-button';
import { WishlistHeaderComponent } from '../components/wishlist-header/wishlist-header';
import { FormsModule } from '@angular/forms';
import { SortAndFilterBarComponent } from '../components/sort-and-filter-bar/sort-and-filter-bar';


export class MyCustomRouteStrategy extends IonicRouteStrategy {
  
  override shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
//    console.log('Strategy check: Navigating from', curr.url, 'to', future.url);
    // 1. Check if the "forceRefresh" data flag exists in the route config
    // or simply check if the paths are the same.
    if (future && curr && future.firstChild && curr.firstChild) {
//      console.log('Strategy check: firstChilds present');
      if (future.firstChild.routeConfig === curr.firstChild.routeConfig && future.firstChild.data['forceRefresh']) {
//        console.log('Strategy check: activating forceRefresh');
        return false; // This forces the component to be destroyed and recreated
      }
    }
    else {
//      console.log('Strategy check: NO firstChilds present');
    }

    // 2. Otherwise, fall back to the standard Ionic behavior
    return super.shouldReuseRoute(future, curr);
  }
}


@NgModule({
  declarations: [
    MyApp, AboutPage, AuthPage, /* LandingPage, */ ApploginPage, /* WebloginPage, */ HomePage, WishlistPage, AnalysesPage,
    FaqPage, AddBeerFormPage,AddBreweryFormPage, BeerDetailPage, ContactPage, SearchPage, SearchBreweryPage,
    SelectorPopoverPage, TabsPage, VenueSelectionPopoverPage,
    NavBarComponent, StatBarComponent, HighestRatedBeersComponent, BeerEventsComponent, BeerHiveLogoComponent,
    BeerListEntryComponent, AddBeerComponent, AddBeerHeaderComponent, AddBreweryHeaderComponent, AnalysesHeaderComponent,
    BeerAnalysisComponent, BeerAnalysisMyFlavorComponent, BeerAnalysisMyMouthfeelComponent, BeerAnalysisMyRatingNotesComponent, BeerAnalysisMySummaryComponent,
    BeerAnalysisGroupComponent, BeerAromasComponent, BeerCcComponent, BeerEventsComponent, BeerHiveComponent, BeerHiveButtonComponent,
    BeerIbaComponent, BeerInfoComponent, BeerSpiderComponent, ColorClarityComponent, FaqViewerComponent, FiltersHeaderComponent,
    FlavorSliderAnalysisComponent, FlavorSliderSearchComponent, FoundBeerComponent, MenuComponent, MyAnalysesComponent, MyNotesComponent,
    RatingBarComponent, SimilarBeersComponent, SmileRateComponent, StatBarComponent, TextSearchBarComponent, WishlistComponent,
    WishlistButtonComponent, WishlistHeaderComponent, SortAndFilterBarComponent
    // ... ALL Pages, Components, Directives DECLARED HERE (keep them all) ...
  ],
  imports: [
    BrowserModule,
    CommonModule,
    // SETUP NGX-TRANSLATE
    TranslateModule.forRoot({
      lang: 'en',
      fallbackLang: 'nl',
      loader: provideTranslateHttpLoader({prefix:"./assets/i18n/",suffix:".json"})
    }),
    BrowserAnimationsModule,
    HttpClientModule, // <-- Added for HttpClient usage in translation factory
    // 8. Correct IonicModule/Routing setup for modern Ionic
    IonicModule.forRoot(), // Removed MyApp reference here. Navigation setup moves to main.ts/routing config.
    RouterModule.forRoot(routes, { 
      preloadingStrategy: PreloadAllModules,
      onSameUrlNavigation: 'reload',
      // enableTracing: true, // <--- Add this line
      bindToComponentInputs: true // <--- ADD THIS LINE
    }),
    IonicStorageModule.forRoot(),
    FormsModule
  ],
  
  // 9. Remove bootstrap and entryComponents (standard Angular CLI handles this via main.ts and routing)
  bootstrap: [MyApp], 
  // entryComponents: [ ... ], // Should largely be removed or simplified if you use Angular Routing
  providers: [
    // StatusBar,
    
    // 10. Only list services HERE if they should be globally available AND are not using providedIn: 'root'
    // If WishlistServiceProvider uses providedIn: 'root', remove it from here.
    Network,
    GooglePlus,
    Facebook,
    AppVersion,
    TranslateService,
    { provide: RouteReuseStrategy, useClass: MyCustomRouteStrategy /* extends IonicRouteStrategy */ },
    BeerDetailPage
  ],
  exports: [ RouterModule ]
})

export class AppModule {
    // 11. In Ionic v4+ / Angular, you often don't need to configure the root module here anymore.
    // If you have Angular Router configured, you might need to add:
    constructor(private router: Router) { } 
}

